import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { ArrowLeft } from "lucide-react"
import { useEquipmentItem, useCreateEquipment, useUpdateEquipment } from "@/hooks/use-equipment"
import { useCompanies } from "@/hooks/use-companies"
import { ApiClientError } from "@/lib/api-client"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"

const equipmentSchema = z.object({
  name: z.string().min(1, "Nazwa jest wymagana"),
  serial_number: z.string().optional(),
  equipment_model: z.string().optional(),
  manufacturer: z.string().optional(),
  category: z.string().optional(),
  purchase_date: z.string().optional(),
  warranty_until: z.string().optional(),
  company_id: z.string().optional(),
  description: z.string().optional(),
  notes: z.string().optional(),
})

type EquipmentForm = z.infer<typeof equipmentSchema>

export function EquipmentFormPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id
  const [error, setError] = useState<string | null>(null)

  const { data: eqData } = useEquipmentItem(id)
  const { data: companiesData } = useCompanies()
  const createMutation = useCreateEquipment()
  const updateMutation = useUpdateEquipment()

  const companies = companiesData?.data ?? []

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EquipmentForm>({
    resolver: zodResolver(equipmentSchema),
    values: isEdit && eqData ? {
      name: eqData.data.name,
      serial_number: eqData.data.serial_number || "",
      equipment_model: eqData.data.model_name || "",
      manufacturer: eqData.data.manufacturer || "",
      category: eqData.data.category || "",
      purchase_date: eqData.data.purchase_date || "",
      warranty_until: eqData.data.warranty_until || "",
      company_id: eqData.data.company?.id || "",
      description: eqData.data.description || "",
      notes: eqData.data.notes || "",
    } : {
      name: "",
      serial_number: "",
      equipment_model: "",
      manufacturer: "",
      category: "",
      purchase_date: "",
      warranty_until: "",
      company_id: "",
      description: "",
      notes: "",
    },
  })

  const onSubmit = async (data: EquipmentForm) => {
    try {
      setError(null)
      const payload: Record<string, unknown> = {
        ...data,
        company_id: data.company_id || null,
        purchase_date: data.purchase_date || null,
        warranty_until: data.warranty_until || null,
      }

      if (isEdit) {
        await updateMutation.mutateAsync({ id, data: payload })
        toast.success("Urządzenie zaktualizowane")
        navigate(`/equipment/${id}`)
      } else {
        const result = await createMutation.mutateAsync(payload)
        toast.success("Urządzenie dodane")
        navigate(`/equipment/${result.data.id}`)
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message)
      } else {
        setError("Wystąpił błąd")
      }
    }
  }

  const inputClass = "w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground outline-none transition-colors focus:ring-2 focus:ring-offset-1"
  const labelClass = "mb-1.5 block text-sm font-medium text-foreground"

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(isEdit ? `/equipment/${id}` : "/equipment")} className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/50">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-foreground">
          {isEdit ? "Edytuj urządzenie" : "Nowe urządzenie"}
        </h1>
      </div>

      {error && (
        <div className="rounded-md border border-red-500 p-3 text-sm text-red-500 bg-red-500/5">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className={labelClass}>Nazwa *</label>
          <input
            {...register("name")}
            className={`${inputClass} ${errors.name ? "border-red-500" : ""}`}
            placeholder="Np. HP LaserJet Pro M404"
          />
          {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Producent</label>
            <input {...register("manufacturer")} className={inputClass} placeholder="Np. HP" />
          </div>
          <div>
            <label className={labelClass}>Model</label>
            <input {...register("equipment_model")} className={inputClass} placeholder="Np. M404dn" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Nr seryjny</label>
            <input {...register("serial_number")} className={inputClass} placeholder="Np. SN123456" />
          </div>
          <div>
            <label className={labelClass}>Kategoria</label>
            <select {...register("category")} className={inputClass}>
              <option value="">Wybierz...</option>
              <option value="drukarka">Drukarka</option>
              <option value="komputer">Komputer</option>
              <option value="serwer">Serwer</option>
              <option value="switch">Switch</option>
              <option value="ups">UPS</option>
              <option value="telefon">Telefon</option>
              <option value="inne">Inne</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Data zakupu</label>
            <input type="date" {...register("purchase_date")} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Gwarancja do</label>
            <input type="date" {...register("warranty_until")} className={inputClass} />
          </div>
        </div>

        <div>
          <label className={labelClass}>Kontrahent</label>
          <select {...register("company_id")} className={inputClass}>
            <option value="">Brak</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Opis</label>
          <textarea {...register("description")} className={inputClass} style={{ minHeight: 80 }} placeholder="Opis urządzenia..." />
        </div>

        <div>
          <label className={labelClass}>Notatki</label>
          <textarea {...register("notes")} className={inputClass} style={{ minHeight: 60 }} placeholder="Dodatkowe notatki..." />
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Zapisywanie..." : isEdit ? "Zapisz zmiany" : "Dodaj urządzenie"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(isEdit ? `/equipment/${id}` : "/equipment")}>
            Anuluj
          </Button>
        </div>
      </form>
    </div>
  )
}
